import type { ContextType } from "react";
import { type Model, type GQL, Utils } from "@binale-tech/shared";
import { BuTaxesSKR } from "@domain/models/BuTaxUtils";
import { type BuTimeframe } from "@domain/context/BuContext";
import { Category } from "@domain/models";
import type { GenericRecord } from "@domain/models/GenericRecord";
import { type ITax, type ItemRecordContext } from "@domain/models/Interfaces";
import type { PaymentsContext } from "@domain/context/accountingData/PaymentsProvider";
import { type ProductKey } from "@domain/models/Product";
import { logger } from "@domain/infrastructure/logger";
import { IstMNFUtils } from "@domain/models/utils/IstMNFUtils";

// Rule: soll - haben

interface SHRecordExtended {
    vatSH: number;
    nettoSH: number;
}

export interface SHItem {
    konto: Model.IExtNum;
    bu: ITax["bu"];
    USt13b?: ITax["USt13b"];
    amount: number;
    isHaben: boolean;
    isTax?: boolean;
    taxType?: "ust" | "vst";
    isRecordKonto?: boolean;
    extended?: SHRecordExtended;
    taxes?: Set<SHItem>;
}

interface SHRecordElement extends SHItem {
    isRecordKonto: boolean;
    extended: SHRecordExtended;
}

interface SHItemElement extends SHItem {
    isTax: boolean;
    taxType: "ust" | "vst";
    taxes: Set<SHItem>;
}

export interface ConverterResponse {
    product: ProductKey;
    items: SHItem[];
}

/**
 * Amount of an item's non-tax leg.
 * - skipTaxLegs (IST FE closing an M-NF Debitor through a non-Geldkonto, e.g. 1370): the full brutto, because no
 *   tax leg follows; the Debitor is closed in full and the USt stays parked on the M-NF account.
 * - IST rebooking (useMNFBalancing) in the ledger: the full brutto, because the payment settles the whole Debitor
 *   and the two tax legs only move the USt from M-NF to fällig. The VAT view keeps the netto as the Kennzahl base.
 * - Otherwise the item netto.
 * @returns Absolute amount in cents
 */
const getItemAmount = (args: {
    itemBrutto: number;
    itemNetto: number;
    skipTaxLegs: boolean | undefined;
    useMNFBalancing: boolean;
    vatMode: boolean | undefined;
}): number => {
    const { itemBrutto, itemNetto, skipTaxLegs, useMNFBalancing, vatMode } = args;
    if (skipTaxLegs || (useMNFBalancing && !vatMode)) {
        return Math.abs(itemBrutto);
    }
    return itemNetto;
};

export class SHConverter {
    constructor(
        protected yearConfig: GQL.ICompanyAccountingYear,
        protected buTimeframes: BuTimeframe[],
        protected payments: ContextType<typeof PaymentsContext>,
    ) {}

    convert(record: GenericRecord, vatMode?: boolean): ConverterResponse {
        const { skr, kontoExt } = this.yearConfig;
        const buTimeframe = BuTaxesSKR.getBuTimeframeYearPeriod(skr, this.buTimeframes, record.year, record.period);
        // console.log("convert", {skr: this.skr, date: record.date, buTimeframe, tf: this.buTimeframes});
        const debug = record.key === "20214080TrI01amg8gjh3jb0fg84nloimuo";
        const recordKonto = record.getRecordCategoryCreditor();
        const productKey = record.getProductKey();
        const recordIsHaben = Utils.SollHaben.isRecordKontoHaben(record.getBrutto(), productKey);
        const brutto = record.getBrutto();
        const bruttoAbs = Math.abs(brutto);
        const netto = Math.abs(record.getNetto(skr));
        const vat = bruttoAbs - netto;
        const recordItem: SHRecordElement = {
            bu: undefined,
            isHaben: recordIsHaben,
            amount: bruttoAbs,
            isTax: false,
            isRecordKonto: true,
            konto: recordKonto,
            extended: {
                vatSH: recordIsHaben ? vat : -vat, // haben stands for minus. if record is haben, then netto and vat are soll.
                nettoSH: recordIsHaben ? netto : -netto,
            },
        };
        const res: ConverterResponse = {
            product: productKey,
            items: [recordItem],
        };
        const itemRecordCtx: ItemRecordContext = {
            recordKonto,
            product: record.getProductKey(),
            year: record.year,
            period: record.period,
        };
        record.items.forEach((v) => {
            const buTax = BuTaxesSKR.getBuTaxYearPeriod(
                v.bu,
                skr,
                record.year,
                record.period,
                buTimeframe ? [buTimeframe] : [],
            );
            const itemKonto = v.getCategoryCreditor();
            const { useMNFBalancing, useUstMNFKonto, skipInVatExport, switchUStBalancingSign, skipTaxLegs } =
                IstMNFUtils.getLogic(record.getProductKey(), this.yearConfig, recordKonto, itemKonto, buTax.bu);
            if (debug) {
            }

            // FE paying an IST M-NF Debitor through an FNR 10 account: the VAT view reports it like Bank (audit H2)
            const vatBaseOnRecordKonto = skipInVatExport && vatMode && useMNFBalancing;
            if (skipInVatExport && vatMode && !vatBaseOnRecordKonto) {
                return;
            }

            const itemIsHaben = Utils.SollHaben.isItemKontoHaben(record.getBrutto(), v.brutto, productKey);
            const itemNetto = Math.abs(v.getNetto(itemRecordCtx, skr));
            const item: SHItemElement = {
                bu: v.bu,
                USt13b: v.USt13b,
                isHaben: vatBaseOnRecordKonto ? !itemIsHaben : itemIsHaben,
                amount: getItemAmount({
                    itemBrutto: v.brutto,
                    itemNetto,
                    skipTaxLegs,
                    useMNFBalancing,
                    vatMode,
                }),
                isTax: false,
                taxType: undefined,
                konto: vatBaseOnRecordKonto ? recordKonto : v.getCategoryCreditor(),
                taxes: new Set(),
            };
            res.items.push(item);
            // IST FE closing an M-NF Debitor through a non-Geldkonto (1370): no Vereinnahmung, the USt stays parked
            if (skipTaxLegs) {
                return;
            }

            // if present only one tax, it's sign is like item (G.Konto) sign
            let isUstHaben = itemIsHaben;
            const isVstHaben = itemIsHaben;
            const taxOnTop = BuTaxesSKR.isTaxOnTop(buTax);
            if (taxOnTop) {
                // if both present (taxOnTop), then VSt is like item, USt is opposite
                isUstHaben = !itemIsHaben;
            }
            // if (useModernFELogic && isItemISTBuMNF) {
            //     isUstHaben = !isUstHaben;
            //     isVstHaben = !isVstHaben;
            // }
            // if (debug) {
            //     console.log({
            //         useMNFBalancing,
            //         useUstMNFKonto,
            //         skipInVatExport,
            //         switchUStBalancingSign,
            //         isUstHaben,
            //         isVstHaben,
            //         buTax,
            //         taxOnTop,
            //     });
            // }
            const amountTaxItem = Math.abs(v.getTax(buTax.percent, taxOnTop));
            if (taxOnTop && BuTaxesSKR.hasExtraTaxToCategory(buTax)) {
                item.amount = itemNetto + amountTaxItem;
            }
            // if (debug) {
            //     console.log({ num: record.num, amountTaxItem, record });
            // }
            const addTaxItem = (
                taxKonto: Model.IExtNum,
                isHaben: boolean,
                bu: Model.Bu,
                taxType: SHItem["taxType"],
            ) => {
                const taxItem: SHItemElement = {
                    isHaben,
                    amount: amountTaxItem,
                    isTax: true,
                    konto: taxKonto,
                    taxes: null,
                    bu,
                    taxType,
                    USt13b: v.USt13b,
                };
                res.items.push(taxItem);
                item.taxes.add(taxItem);
            };
            const logError = (...params: any) => {
                logger.error("integrity error, no category for bu tax num", ...params);
                logger.log(
                    "record date",
                    record.date,
                    "butf_id",
                    buTimeframe.id,
                    "size",
                    buTimeframe.defaultCategories.size,
                );
            };

            // bu has M-Konto (ust). ust MNF is always under the ust
            if (buTax.ust) {
                const ustMNFKonto = Category.copyWithFixedNum(
                    buTimeframe.defaultCategories.get(buTax.ustMNF),
                    kontoExt,
                );
                if (buTax.ustMNF && !ustMNFKonto) {
                    logError("buTax.ustMNF", buTax.ust);
                }
                const ustKonto = Category.copyWithFixedNum(buTimeframe.defaultCategories.get(buTax.ust), kontoExt);
                if (buTax.ust && !ustKonto) {
                    logError("ust", buTax.ust);
                }

                const ustSign = switchUStBalancingSign ? !isUstHaben : isUstHaben;

                const ustTaxKonto = useUstMNFKonto ? ustMNFKonto : ustKonto;
                addTaxItem(ustTaxKonto, ustSign, buTax.bu, "ust");

                // IST-UstMNF logic for balancing tax for the Payment Representation Records (Bank, KB, FE)
                // https://binale.atlassian.net/wiki/spaces/SPECS/pages/195624961/IST+Versteuerung
                if (buTax.ustMNF && useMNFBalancing) {
                    addTaxItem(ustKonto, !ustSign, buTax.bu, "ust");
                }
            }

            if (buTax.vst) {
                const vstKonto = Category.copyWithFixedNum(buTimeframe.defaultCategories.get(buTax.vst), kontoExt);
                if (buTax.vst && !vstKonto) {
                    logError("vst", buTax.vst);
                }
                addTaxItem(vstKonto, isVstHaben, buTax.bu, "vst");
            }
        });
        return res;
    }

    getAllItems(record: GenericRecord): SHItem[] {
        const conv = this.convert(record);
        return [...conv.items];
    }

    getItemsVatMode(record: GenericRecord): SHItem[] {
        const conv = this.convert(record, true);
        return [...conv.items];
    }
}
